import { useCallback, useEffect, useState } from "react";

export type Theme = "light" | "dark";

const STORAGE_KEY = "uvi.theme";

/**
 * Pure read of the stored preference, kept separate from the hook below so
 * it is testable without any DOM/React rendering infra (this project's
 * vitest config runs `.test.ts` files under node, not jsdom — there is no
 * component-test setup to render a hook through).
 */
export function readStoredTheme(storage: Pick<Storage, "getItem">): Theme {
  try {
    return storage.getItem(STORAGE_KEY) === "dark" ? "dark" : "light";
  } catch {
    // Private browsing / blocked storage: fall back to light rather than
    // throwing out of a render.
    return "light";
  }
}

export function otherTheme(theme: Theme): Theme {
  return theme === "light" ? "dark" : "light";
}

/**
 * Applies the theme to the document and persists it. Pure client-side —
 * there is no backend concept of a theme preference, so this never touches
 * the API.
 */
export function useTheme(): { theme: Theme; toggleTheme: () => void } {
  const [theme, setTheme] = useState<Theme>(() => readStoredTheme(window.localStorage));

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    try {
      window.localStorage.setItem(STORAGE_KEY, theme);
    } catch {
      // Storage blocked: the attribute above still applies for this page
      // load, it just won't persist across reloads.
    }
  }, [theme]);

  const toggleTheme = useCallback(() => setTheme(otherTheme), []);

  return { theme, toggleTheme };
}
